
import { supabase } from '@/integrations/supabase/client';
import { Profile } from '@/components/profile/useProfiles';

export interface EvaluationResult {
  id?: string;
  score: number;
  interpretation: string;
  resultCategory: string;
  resultIconType: 'smile' | 'meh' | 'frown';
  resultColor: string;
  profileId: string;
  userId: string;
  latitude?: number | null;
  longitude?: number | null;
  eyesAnalysis?: any;
  nailsAnalysis?: any;
  createdAt?: Date;
}

export const saveEvaluationResult = async (result: EvaluationResult) => {
  try {
    // First save the main evaluation
    const { data: evaluationData, error: evaluationError } = await supabase
      .from('photo_evaluations')
      .insert({
        user_id: result.userId,
        profile_id: result.profileId,
        score: result.score,
        interpretation: result.interpretation,
        result_category: result.resultCategory,
        latitude: result.latitude || null,
        longitude: result.longitude || null
      })
      .select()
      .single();
      
    if (evaluationError) throw evaluationError;
    
    // Save eyes analysis details if available
    if (result.eyesAnalysis && result.eyesAnalysis.data && result.eyesAnalysis.data.predictions) {
      const eyesPredictions = result.eyesAnalysis.data.predictions;
      
      for (const prediction of eyesPredictions) {
        await supabase
          .from('evaluation_details')
          .insert({
            evaluation_id: evaluationData.id,
            detail_type: 'eyes',
            detected_class: prediction.class,
            confidence: prediction.confidence,
            notes: `Coordenadas: x=${prediction.x}, y=${prediction.y}, width=${prediction.width}, height=${prediction.height}`
          });
      }
      
      // Save the image to storage if available
      if (result.eyesAnalysis.image) {
        const base64Data = result.eyesAnalysis.image.split(',')[1];
        const { data: userId } = await supabase.auth.getUser();
        
        if (userId.user) {
          await supabase.storage
            .from('evaluation_images')
            .upload(
              `${userId.user.id}/${evaluationData.id}/eyes.jpg`,
              base64ToBlob(base64Data),
              {
                contentType: 'image/jpeg'
              }
            );
        }
      }
    }
    
    // Save nails analysis details if available
    if (result.nailsAnalysis && result.nailsAnalysis.data && result.nailsAnalysis.data.predictions) {
      const nailsPredictions = result.nailsAnalysis.data.predictions;
      
      for (const prediction of nailsPredictions) {
        await supabase
          .from('evaluation_details')
          .insert({
            evaluation_id: evaluationData.id,
            detail_type: 'nails',
            detected_class: prediction.class,
            confidence: prediction.confidence,
            notes: `Coordenadas: x=${prediction.x}, y=${prediction.y}, width=${prediction.width}, height=${prediction.height}`
          });
      }
      
      // Save the image to storage if available
      if (result.nailsAnalysis.image) {
        const base64Data = result.nailsAnalysis.image.split(',')[1];
        const { data: userId } = await supabase.auth.getUser();
        
        if (userId.user) {
          await supabase.storage
            .from('evaluation_images')
            .upload(
              `${userId.user.id}/${evaluationData.id}/nails.jpg`,
              base64ToBlob(base64Data),
              {
                contentType: 'image/jpeg'
              }
            );
        }
      }
    }
    
    return evaluationData;
  } catch (error) {
    console.error('Error saving evaluation result:', error);
    throw error;
  }
};

export const getEvaluationHistory = async (profileId: string) => {
  try {
    // Validar si el ID es un UUID válido o es el ID por defecto "1"
    const isValidUUID = profileId === '1' || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(profileId);
    
    if (!isValidUUID) {
      throw new Error('ID de perfil inválido');
    }
    
    // Si es el perfil por defecto, no devolvemos evaluaciones
    if (profileId === '1') {
      return [];
    }
    
    console.log('Buscando evaluaciones para perfil ID:', profileId);
    
    const { data, error } = await supabase
      .from('photo_evaluations')
      .select('*')
      .eq('profile_id', profileId)
      .order('created_at', { ascending: false });
      
    if (error) {
      console.error('Error en consulta Supabase:', error);
      throw error;
    }
    
    console.log('Evaluaciones encontradas:', data?.length || 0);
    
    return data || [];
  } catch (error) {
    console.error('Error getting evaluation history:', error);
    throw error;
  }
};

// Helper function to convert base64 to Blob
const base64ToBlob = (base64: string): Blob => {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  
  return new Blob([bytes], { type: 'image/jpeg' });
};
